import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { CreditCard, Lock } from 'lucide-react';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const StripePaymentForm = ({ amount, onSuccess, onCancel, bookingDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }

      // Call success callback with payment method ID
      onSuccess(paymentMethod.id);
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Error',
        description: 'Error al procesar el pago',
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Información de Pago
        </CardTitle>
        <p className="text-sm text-gray-600">
          Se realizará un cargo de seguridad (hold) de ${amount} MXN. 
          Solo se cobrará si el Mobilitier aprueba tu reserva.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 border rounded-lg bg-gray-50">
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>

          <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-2">
            <Lock className="w-4 h-4 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold">Pago seguro con Stripe</p>
              <p className="text-xs mt-1">
                Tu información está protegida con encriptación de nivel bancario
              </p>
            </div>
          </div>

          {bookingDetails && (
            <div className="border-t pt-4 space-y-2">
              <h4 className="font-semibold">Resumen de la Reserva</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Auto:</span>
                  <span>{bookingDetails.carName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Fechas:</span>
                  <span>{bookingDetails.dates}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Días:</span>
                  <span>{bookingDetails.days}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                  <span>Total:</span>
                  <span className="text-blue-600">${amount} MXN</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={processing}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!stripe || !cardComplete || processing}
              className="flex-1 bg-[#000000] hover:bg-ink-700"
            >
              {processing ? 'Procesando...' : 'Confirmar y Pagar'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default StripePaymentForm;
